import {isCSR} from '@jobseeker/lotus/utils';
import {NextSeo, NextSeoProps} from 'next-seo';
import {getMeta, getSyncPageTitle, getSyncPageDescription} from '@utils/seo';

type SEOProps = {
  seoConfig?: NextSeoProps;
  paginationEnabled?: boolean;
};

const SeoLayout = ({seoConfig, paginationEnabled = false}: SEOProps) => {
  const notAllowedIndexAndFollow =
    process.env.NEXT_PUBLIC_NO_INDEX === 'true' ? true : false;

  if (isCSR() && paginationEnabled && seoConfig) {
    const keyToDelete = ['prev', 'next'];

    seoConfig.additionalLinkTags = seoConfig.additionalLinkTags?.filter(
      tag => !keyToDelete.includes(tag.rel)
    );
  }

  return (
    <NextSeo
      {...seoConfig}
      nofollow={notAllowedIndexAndFollow || Boolean(seoConfig?.nofollow)}
      noindex={notAllowedIndexAndFollow || Boolean(seoConfig?.noindex)}
    />
  );
};

type SeoIntrupProps = {
  pageNo: number;
  nextPage: boolean;
  type:
    | 'jobfeed'
    | 'jobdetail'
    | 'reset-password'
    | 'allnotification'
    | 'recruiterview'
    | 'epoch'
    | 'personalize-jobfeed'
    | 'interviews'
    | 'unsubscribe'
    | 'women_at_work'
    | 'course'
    | 'unpublished-course-detail'
    | 'unpublished-course-list'
    | 'notfound'
    | 'reactivate'
    | 'pendingassessments'
    | 'pro_talent'
    | 'consent'
    | 'x-clause'
    | 'cv-analyzer';
  title?: string;
  description?: string;
};

const SeoIntrup = ({
  pageNo,
  nextPage,
  type,
  title,
  description,
}: SeoIntrupProps) => {
  const currentUrl = window.location.href?.split('?')[0];
  const notAllowedIndexAndFollow =
    process.env.NEXT_PUBLIC_NO_INDEX === 'true' ? true : false;
  const noIndexNoFollow = () => ({
    title,
    description,
    canonical: currentUrl,
    noindex: true,
    nofollow: true,
  });

  const Intrup: {
    [key: string]: () => object;
  } = {
    ['jobfeed']: () => {
      const descriptionContent = description || getMeta('description') || '';

      const seoUpdate: NextSeoProps = {
        title: getSyncPageTitle(title || document.title, pageNo),
        canonical: pageNo > 1 ? `${currentUrl}?page=${pageNo}` : currentUrl,
      };

      if (descriptionContent) {
        seoUpdate['description'] = getSyncPageDescription(
          descriptionContent,
          pageNo
        );
      }
      const match = title?.match(/(\d+)\s+Job Vacancies/i);
      const jobCount = match ? parseInt(match[1], 10) : 0;

      if (pageNo <= 1) {
        if (jobCount >= 20) {
          seoUpdate['additionalLinkTags'] = [
            {
              rel: 'next',
              href: `${currentUrl}?page=${
                pageNo < 1 ? pageNo + 2 : pageNo + 1
              }`,
            },
          ];
        }
      } else if (pageNo > 1 && nextPage) {
        seoUpdate['additionalLinkTags'] = [
          {
            rel: 'prev',
            href:
              pageNo - 1 <= 1 ? currentUrl : `${currentUrl}?page=${pageNo - 1}`,
          },
          {
            rel: 'next',
            href:
              pageNo + 1 === 1
                ? `${currentUrl}`
                : `${currentUrl}?page=${pageNo + 1}`,
          },
        ];
      } else if (!nextPage) {
        if (jobCount >= 20) {
          seoUpdate['additionalLinkTags'] = [
            {
              rel: 'prev',
              href:
                pageNo - 1 <= 1
                  ? currentUrl
                  : `${currentUrl}?page=${pageNo - 1}`,
            },
          ];
        }
      }

      return seoUpdate;
    },
    ['reset-password']: () => {
      return {
        title: 'Reset Password | iimjobs.com',
      };
    },
    ['allnotification']: () => {
      return {
        title: 'Notifications',
        canonical: currentUrl,
      };
    },
    ['recruiterview']: () => ({
      title: 'Recruiter View',
      canonical: currentUrl,
    }),
    ['epoch']: () => {
      return {
        title: 'EPOCH Case Challenge',
      };
    },
    ['x-clause']: () => {
      return {
        title: 'X-Clause Case Challenge',
      };
    },
    ['unsubscribe']: () => {
      return {
        title: 'Unsubscribe Email',
        canonical: currentUrl,
      };
    },
    ['personalize-jobfeed']: () => {
      return {
        title: 'Personalize Jobfeed',
        canonical: currentUrl,
      };
    },
    ['interviews']: () => {
      return {
        title: title ?? 'Interviews',
        canonical: currentUrl,
      };
    },
    ['women_at_work']: () => {
      return {
        title: 'Women At Work',
        canonical: currentUrl,
      };
    },
    ['notfound']: () => {
      return {
        title: title ?? '404 - Not Found',
        noindex: true,
        nofollow: true,
      };
    },
    ['pro_talent']: () => {
      return {
        title: 'Pro Talent',
        canonical: currentUrl,
      };
    },
    ['course']: () => {
      return {
        title,
        description,
        canonical: currentUrl,
      };
    },
    ['unpublished-course-detail']: noIndexNoFollow,
    ['unpublished-course-list']: noIndexNoFollow,
    ['reactivate']: () => {
      return {
        title: 'Reactivate Account',
        canonical: currentUrl,
      };
    },
    ['pendingassessments']: () => {
      return {
        title: 'Pending Assessments',
        canonical: currentUrl,
      };
    },
    ['consent']: () => {
      return {
        title: `Consent - ${process.env.NEXT_PUBLIC_APP_DOMAIN}`,
        canonical: currentUrl,
        noindex: true,
        nofollow: true,
      };
    },
    ['cv-analyzer']: () => {
      return {
        title: title ?? 'Instant CV Review | CV Analyzer - hirist.tech',
        description:
          description ??
          'Get instant insights to take your CV to the next level. Analyze your resume for ATS optimization, skills relevance, and content quality.',
        canonical: currentUrl,
      };
    },
  };

  const getSeoIntrupByType = (type: string) => Intrup[type]();

  const intrupSeo = getSeoIntrupByType(type) as NextSeoProps;

  return (
    <NextSeo
      {...intrupSeo}
      nofollow={notAllowedIndexAndFollow || Boolean(intrupSeo.nofollow)}
      noindex={notAllowedIndexAndFollow || Boolean(intrupSeo.noindex)}
    />
  );
};

export {SeoLayout, SeoIntrup};
